import moment from "moment";
import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import ToggleButton from "src/components/common/Buttons/ToggleButton";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { InstitutionPersonnelsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionActions from "../../../actions/institution";
import * as NavigationActions from "../../../actions/navigation";
import * as ToggleActions from "../../../actions/pages/togglePage";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import * as TrialPersonnelActions from "../../../actions/trialPersonnel";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { InstitutionSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import { TogglePageItem } from "../../../reducers/pages/togglePage";

import "./InstitutionPersonnelsPage.scss";

interface InstitutionPersonnelsPageProps {
    institution: Dtos.Institution;
    loadingInstitution: boolean;
    loadInstitutionSuccess: boolean;
    loadInstitutionFailure: boolean;
    loadInstitutionErrorCode: string;
    trialPersonnel: Dtos.TrialPersonnel[];
    loadingTrialPersonnel: boolean;
    loadTrialPersonnelSuccess: boolean;
    loadTrialPersonnelFailure: boolean;
    loadTrialPersonnelErrorCode: string;
    trialInstitutions: Dtos.TrialInstitution[];
    loadingTrialInstitutions: boolean;
    loadTrialInstitutionsSuccess: boolean;
    loadTrialInstitutionsFailure: boolean;
    loadTrialInstitutionsErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    toggleItems: TogglePageItem[];
    clearInstitution: InstitutionActions.IInstitutionClearActionCreator;
    loadInstitution: InstitutionActions.IInstitutionLoadByIdActionCreator;
    clearTrialPersonnel: TrialPersonnelActions.ITrialPersonnelClearActionCreator;
    loadTrialPersonnel: TrialPersonnelActions.ITrialPersonnelsLoadByInstitutionIdActionCreator;
    clearTrialInstitutions: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitutions: TrialInstitutionActions.ITrialInstitutionLoadActionCreator;
    toggleExpand: ToggleActions.IToggleExpandedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const InstitutionPersonnelsPage: FC<InstitutionPersonnelsPageProps> = ({
    institution,
    loadingInstitution,
    loadInstitutionSuccess,
    loadInstitutionFailure,
    loadInstitutionErrorCode,
    trialPersonnel,
    loadingTrialPersonnel,
    loadTrialPersonnelSuccess,
    loadTrialPersonnelFailure,
    loadTrialPersonnelErrorCode,
    trialInstitutions,
    loadingTrialInstitutions,
    loadTrialInstitutionsSuccess,
    loadTrialInstitutionsFailure,
    loadTrialInstitutionsErrorCode,
    permissions,
    toggleItems,
    clearInstitution,
    loadInstitution,
    clearTrialPersonnel,
    loadTrialPersonnel,
    clearTrialInstitutions,
    loadTrialInstitutions,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{ institutionId: string }>();
    const institutionIdNumber = parseInt(institutionId, 10);

    //
    // -- States --
    const [showCeased, setShowCeased] = useState<boolean>(false);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showInstitutionPersonnels =
        !loadingInstitution &&
        loadInstitutionSuccess &&
        !loadingTrialPersonnel &&
        loadTrialPersonnelSuccess &&
        !loadingTrialInstitutions &&
        loadTrialInstitutionsSuccess &&
        !creatingTree;


    // Load Institution & Personnel
    useEffect(() => {
        if (institutionIdNumber && !institution) {
            setDrawerItems(
                InstitutionSubMenu(institutionIdNumber, permissions)
            );
            setBreadcrumbs(
                InstitutionPersonnelsBreadcrumbs(institutionIdNumber)
            );

            setTreeData(null);

            loadInstitution(institutionIdNumber);
            loadTrialPersonnel(institutionIdNumber, undefined);
            loadTrialInstitutions();
        }
    }, [
        institutionIdNumber,
        institution,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadInstitution,
        InstitutionSubMenu,
        InstitutionPersonnelsBreadcrumbs,
        setTreeData,
        loadTrialPersonnel,
        loadTrialInstitutions
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionFailure) {
            navigate(`/error/${loadInstitutionErrorCode}`);
        }
        if (loadTrialPersonnelFailure) {
            navigate(`/error/${loadTrialPersonnelErrorCode}`);
        }
        if (loadTrialInstitutionsFailure) {
            navigate(`/error/${loadTrialInstitutionsErrorCode}`);
        }
    }, [
        loadInstitutionFailure,
        loadInstitutionErrorCode,
        loadTrialPersonnelFailure,
        loadTrialPersonnelErrorCode,
        loadTrialInstitutionsFailure,
        loadTrialInstitutionsErrorCode,
        navigate
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (institutionIdNumber && institution?.name) {
            setDrawerItems(
                InstitutionSubMenu(institutionIdNumber, permissions)
            );
            setBreadcrumbs(
                InstitutionPersonnelsBreadcrumbs(
                    institutionIdNumber,
                    institution.name
                )
            );
        }
    }, [
        institution,
        institutionIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        InstitutionSubMenu,
        InstitutionPersonnelsBreadcrumbs,
    ]);

    // Cleanup effect to clear the institution on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearInstitution();
            clearTrialPersonnel();
            clearTrialInstitutions();
        };
    }, [clearInstitution, clearTrialPersonnel, clearTrialInstitutions, setTreeData]);

    // Create tree
    useEffect(() => {
        if (trialPersonnel && !initalizedTreeData) {
            setCreatingTree(true);

            // If no institution personnel return
            if (trialPersonnel?.length == 0) {
                setCreatingTree(false);
                return;
            }

            const createRoleNodes = (personnel: Dtos.TrialPersonnel[], showCeased: boolean, nodeIdPrefix: string) => {
                let uniqueRoles: Dtos.Role[] = Array.from(
                    new Map(
                        personnel
                            ?.filter((x) => showCeased ? true : !x.dateCeased)
                            ?.map((x) => [x.role.id, x.role])
                    ).values()
                );

                let children =
                    uniqueRoles
                        ?.sort((a, b) => a?.value?.localeCompare(b?.value))
                        ?.map((x) => ({
                            id: `${nodeIdPrefix}-role-${x.id}`,
                            data: (
                                <div className="d-flex align-items-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "25px" }}
                                    >
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ROLE} />
                                    </div>
                                    <span
                                        className="pl-4 mb-0"
                                        style={{ width: "275px" }}
                                    >
                                        {x?.value}
                                    </span>
                                    <div
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ width: "50px" }}
                                    >
                                        <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                            {
                                                personnel?.filter((y) =>
                                                    showCeased
                                                        ? y.roleId == x.id
                                                        : y.roleId == x.id &&
                                                        !y.dateCeased
                                                )?.length
                                            }
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: true,
                            subTree: {
                                children: [...createPersonnelNodes(personnel?.filter((z) => z.roleId == x.id), showCeased, `${nodeIdPrefix}-role-${x.id}`)],
                            },
                        })) ?? [];

                return children;
            };

            const createPersonnelNodes = (personnel: Dtos.TrialPersonnel[], showCeased: boolean, nodeIdPrefix: string) => {
                let children =
                    personnel
                        ?.filter((x) => showCeased ? true : !x.dateCeased)
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((x) => ({
                            id: `${nodeIdPrefix}-personnel-${x.id}`,
                            data: (
                                <div className={`form-row ${showCeased ? x?.dateCeased ? "ceased" : "" : ""}`}>
                                    <div className="col-1">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.USER} />
                                    </div>
                                    <div className="col">
                                        {x?.personnel?.displayWithSalutation}
                                    </div>
                                    <div className="col">
                                        {
                                            x?.dateStarted
                                                ? moment(x?.dateStarted, "YYYY-MM-DD HH:mm:ss.SSSSSSS Z").format("DD/MM/YYYY")
                                                : ""
                                        }
                                    </div>
                                    <div className="col">
                                        {
                                            x?.dateCeased
                                                ? moment(x?.dateCeased, "YYYY-MM-DD HH:mm:ss.SSSSSSS Z").format("DD/MM/YYYY")
                                                : ""
                                        }
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: `tableheader`,
                        data: (
                            <div className="form-row">
                                <div className="col-1"></div>
                                <div className="col">
                                    <strong>Name</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes = [];

            let uniqueTrials: Dtos.Trial[] = Array.from(
                new Map(
                    trialPersonnel
                        ?.filter((x) => x?.trial && (showCeased ? true : !x.dateCeased))
                        ?.map((x) => [x.trial.id, x.trial])
                ).values()
            );

            let trialNodes =
                uniqueTrials
                    ?.sort((a, b) => a?.studyAcronym.localeCompare(b?.studyAcronym))
                    ?.map((x) => ({
                        id: `trial-${x.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "25px" }}
                                >
                                    <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.TRIAL} />
                                </div>
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "275px" }}
                                >
                                    {x?.studyAcronym}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {
                                            trialPersonnel?.filter((y) =>
                                                showCeased
                                                    ? y.trialId == x.id
                                                    : y.trialId == x.id &&
                                                    !y.dateCeased
                                            )?.length
                                        }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createRoleNodes(trialPersonnel?.filter((z) => z.trialId == x.id), showCeased, `trial-${x.id}`)],
                        },
                    })) ?? [];

            treeNodes = [
                ...trialNodes,
                ...createRoleNodes(trialPersonnel?.filter((x) => !x.trialId), showCeased, '')
            ];

            // Initialize tree toggle
            const expanded = treeNodes?.filter((x) => x.id.includes('trial'))?.map((x) => x.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialPersonnel,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        showCeased,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("personnel") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.InstitutionPersonnelView
                    )
                ) {
                    const tpId = id.substring(id.lastIndexOf("-") + 1);
                    const personnel = trialPersonnel.find((x) => x.id.toString() == tpId);

                    if (personnel?.trialId) {
                        const trialinstitution = trialInstitutions.find((x) => x.trialId == personnel.trialId && x.institutionId == personnel.institutionId);
                        navigate(`/trials/${personnel?.trialId}/trialinstitution/${trialinstitution?.id}/personnel/${tpId}/details`);
                    }
                    else {
                        navigate(`/institutions/${institutionIdNumber}/personnel/${tpId}/details`);
                    }
                }
                else {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [
            setToggleState,
            navigate,
            institutionIdNumber,
            permissions,
            institution,
            trialPersonnel,
            trialInstitutions
        ]
    );

    const onChange = (newValue) => {
        setInitalizedTreeData(false);
        setShowCeased(newValue);
    };

    // Refresh tree
    useEffect(() => {
        if (trialPersonnel) {
            setInitalizedTreeData(false);
        }
    }, [trialPersonnel, setInitalizedTreeData]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className="col-md-12 col-lg-auto mb-2">
                        <h1>Institution Personnel</h1>
                    </div>
                    <div
                        className="col-md-12 col-lg d-flex justify-content-md-start justify-content-lg-end align-items-stretch mb-2 mb-lg-0"
                        style={{ height: "38px" }}
                    >
                        {institution &&
                            institution.id > 0 &&
                            AuthenticationHelper.hasPermission(
                                permissions,
                                Dtos.Permission.InstitutionPersonnelCreate,
                                null,
                                institution.id
                            ) && (
                                <>
                                    <ToggleButton
                                        id="toggle-button-ceased"
                                        checked={showCeased}
                                        onChange={onChange}
                                        labelA="Hide ceased personnel"
                                        labelB="Show ceased personnel"
                                        style={{ minWidth: "200px" }}
                                    />
                                    <a
                                        className="btn btn-info mr-2"
                                        target="_blank"
                                        href={encodeURI(
                                            `/api/personnelrole/export/0/${institution.id.toString()}/0/0`
                                        )}
                                    >
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.CLOUD_DOWNLOAD} fixedWidth />
                                    </a>
                                    <Link
                                        className={"btn btn-primary"}
                                        url={`/institutions/${institution.id}/personnel/create`}
                                    >
                                        Create
                                    </Link>
                                </>
                            )}
                    </div>
                </div>
                {!showInstitutionPersonnels ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showInstitutionPersonnels ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                                    </div>
                                    <span>There are currently no institution personnel</span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        institution,
        showInstitutionPersonnels,
        showCeased,
        toggleItems,
        treeData,
        handleToggle,
        toggleState,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.InstitutionPersonnelView}
            loading={
                loadingInstitution ||
                loadingTrialPersonnel ||
                loadingTrialInstitutions
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionPersonnelsPageProps> => {
    let institution: Dtos.Institution | undefined = !(
        state.institutions.formData instanceof Array
    )
        ? state.institutions.formData
        : undefined;
    let trialPersonnel: Dtos.TrialPersonnel[] | undefined = (
        state.trialPersonnel.formData instanceof Array
    )
        ? state.trialPersonnel.formData
        : undefined;
    let trialInstitutions: Dtos.TrialInstitution[] | undefined = (
        state.trialInstitution.formData instanceof Array
    )
        ? state.trialInstitution.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institution: institution!,
        loadingInstitution:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Pending,
        loadInstitutionSuccess:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Success,
        loadInstitutionFailure:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Failure,
        loadInstitutionErrorCode: state.institutions.loadState.errorCode,
        trialPersonnel: trialPersonnel!,
        loadingTrialPersonnel:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Pending,
        loadTrialPersonnelSuccess:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Success,
        loadTrialPersonnelFailure:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Failure,
        loadTrialPersonnelErrorCode: state.trialPersonnel.loadState.errorCode,
        trialInstitutions: trialInstitutions!,
        loadingTrialInstitutions:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionsSuccess:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionsFailure:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionsErrorCode: state.trialInstitution.loadState.errorCode,
        permissions: permissions,
        toggleItems: state.togglePage.items,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<InstitutionPersonnelsPageProps> => {
    return {
        loadInstitution: bindActionCreators(InstitutionActions.LoadInstitutionById, dispatch),
        clearInstitution: bindActionCreators(InstitutionActions.Clear, dispatch),
        loadTrialPersonnel: bindActionCreators(TrialPersonnelActions.LoadTrialPersonnelsByInstitutionId, dispatch),
        clearTrialPersonnel: bindActionCreators(TrialPersonnelActions.Clear, dispatch),
        loadTrialInstitutions: bindActionCreators(TrialInstitutionActions.LoadTrialInstitution, dispatch),
        clearTrialInstitutions: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
        toggleExpand: bindActionCreators(ToggleActions.ToggleExpanded, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionPersonnelsPage);
